import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { readViewportCapabilities } from './readCapabilities'
import {
  DESKTOP_VIEWPORT_FALLBACK,
  type ViewportCapabilities,
  type ViewportLayout,
} from './tokens'

const ViewportContext = createContext<ViewportCapabilities>(DESKTOP_VIEWPORT_FALLBACK)
const ViewportLayoutContext = createContext<ViewportLayout>(pickLayout(DESKTOP_VIEWPORT_FALLBACK))

type Props = {
  children: ReactNode
}

function pickLayout(capabilities: ViewportCapabilities): ViewportLayout {
  return {
    layoutMode: capabilities.layoutMode,
    pointerMode: capabilities.pointerMode,
    orientation: capabilities.orientation,
    isCompactTablet: capabilities.isCompactTablet,
  }
}

function layoutsEqual(a: ViewportLayout, b: ViewportLayout) {
  return (
    a.layoutMode === b.layoutMode &&
    a.pointerMode === b.pointerMode &&
    a.orientation === b.orientation &&
    a.isCompactTablet === b.isCompactTablet
  )
}

function capabilitiesEqual(a: ViewportCapabilities, b: ViewportCapabilities) {
  return (
    layoutsEqual(a, b) &&
    a.width === b.width &&
    a.height === b.height &&
    a.safeArea.top === b.safeArea.top &&
    a.safeArea.bottom === b.safeArea.bottom &&
    a.safeArea.left === b.safeArea.left &&
    a.safeArea.right === b.safeArea.right
  )
}

/** Re-provide Planner capabilities inside R3F Canvas (new React root). No extra listeners. */
export function ViewportValueProvider({
  value,
  children,
}: {
  value: ViewportCapabilities
  children: ReactNode
}) {
  return <ViewportContext.Provider value={value}>{children}</ViewportContext.Provider>
}

export function ViewportProvider({ children }: Props) {
  const [capabilities, setCapabilities] = useState<ViewportCapabilities>(() =>
    readViewportCapabilities(),
  )
  const [layout, setLayout] = useState<ViewportLayout>(() => pickLayout(capabilities))

  useEffect(() => {
    const update = () => {
      const next = readViewportCapabilities()
      setLayout((prev) => (layoutsEqual(prev, next) ? prev : pickLayout(next)))
      setCapabilities((prev) => (capabilitiesEqual(prev, next) ? prev : next))
    }

    window.addEventListener('resize', update)
    window.addEventListener('orientationchange', update)

    const pointerQuery = window.matchMedia('(pointer: coarse)')
    const hoverQuery = window.matchMedia('(hover: none)')
    pointerQuery.addEventListener('change', update)
    hoverQuery.addEventListener('change', update)

    update()

    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('orientationchange', update)
      pointerQuery.removeEventListener('change', update)
      hoverQuery.removeEventListener('change', update)
    }
  }, [])

  useEffect(() => {
    const compactTablet = layout.isCompactTablet
    document.documentElement.classList.toggle('compact-tablet-ui', compactTablet)
    return () => {
      document.documentElement.classList.toggle(
        'compact-tablet-ui',
        readViewportCapabilities().isCompactTablet,
      )
    }
  }, [layout.isCompactTablet])

  const capabilitiesValue = useMemo(() => capabilities, [capabilities])
  const layoutValue = useMemo(() => layout, [layout])

  return (
    <ViewportLayoutContext.Provider value={layoutValue}>
      <ViewportContext.Provider value={capabilitiesValue}>{children}</ViewportContext.Provider>
    </ViewportLayoutContext.Provider>
  )
}

export function useViewportCapabilities(): ViewportCapabilities {
  return useContext(ViewportContext)
}

/** layoutMode / pointer / tablet. Does not update on width/height ticks. */
export function useViewportLayout(): ViewportLayout {
  return useContext(ViewportLayoutContext)
}

/** R3F Canvas is a new React root; re-provide layout so compact camera/lights work inside it. */
export function CanvasViewportLayout({
  value,
  children,
}: {
  value: ViewportLayout
  children: ReactNode
}) {
  return <ViewportLayoutContext.Provider value={value}>{children}</ViewportLayoutContext.Provider>
}
